import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Input, DatePicker, Select, Spin, Tooltip, Image } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplates } from '../../slices/templatesSlice';
import dayjs from 'dayjs';
import { useNotification } from '../../Helper/NotificationProvider';
import { InfoCircleOutlined } from '@ant-design/icons';
import store from '../../store';
import TemplatePreview from '../TemplatePreview/TemplatePreview';

const { Option } = Select;

const EventModal = (props) => {
  const {
    isModalVisible,
    setIsModalVisible,
    handleFormSubmit,
    initialValues = {}
  } = props;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { openNotification } = useNotification();
  const templates = useSelector(state => state.templates.data);
  const templatesStatus = useSelector(state => state.templates.status);
  const locations = useSelector(state => state.locations.data);
  const { RangePicker } = DatePicker;
  const user = store.getState().auth.user;
  const customers = useSelector(state => state.customers.data);
  const selectedCustomer = store.getState().customers.selectedCustomer || user?.tenantId;
  const customer = customers.find(customer => customer.key === selectedCustomer);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [form] = Form.useForm(); //

  const handleTemplateChange = (value, option) => {
    form.setFieldsValue({ template: option });
    setSelectedTemplate(option?.label);
  };

  useEffect(() => {
    if (templatesStatus === 'idle') {
      dispatch(fetchTemplates());
    }
  }, [templatesStatus, dispatch]);

  useEffect(() => {
    if (isModalVisible) {
      // Set the initial form values when the modal is visible
      if (initialValues && Object.keys(initialValues).length > 0) {
        form.setFieldsValue({
          ...initialValues,
          location: initialValues.location,
          timeRange:
            initialValues.startTime && initialValues.endTime
              ? [dayjs(initialValues.startTime), dayjs(initialValues.endTime)]
              : []
        });
      } else {
        form.resetFields();
      }
    }
  }, [isModalVisible, initialValues]);

  return (
    <Modal
      title={initialValues?.id ? "Event bearbeiten" : "Event hinzufügen"} // Dynamically update the title
      visible={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          handleFormSubmit(values);
          form.resetFields(); // Reset form after submission
        }}
        initialValues={initialValues || {}} // Prepopulate the form with initialValues
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Bitte Name des Events eingeben" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Location"
          name="location"
          rules={[{ required: true, message: "Bitte Location auswählen" }]}
        >
          {loading ? (
            <Spin size="small" />
          ) : (
            <Select placeholder="Location auswählen">
              {locations.map((location) => (
                <Option key={location._id} value={location._id}>
                  {location.name}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item
          label="Zeitraum"
          name="timeRange"
          rules={[{ required: true, message: "Bitte einen Zeitraum auswählen" }]}
        >
          <RangePicker
            showTime
            format="DD.MM.YYYY HH:mm"
            style={{ width: '100%' }}
            placeholder={["Startzeit", "Endzeit"]}
          />
        </Form.Item>

        <Form.Item
          label="Beschreibung"
          name="description"
          rules={[{ required: false, message: "Bitte Beschreibung eingeben" }]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          label={
            <>
              Template
              <Tooltip overlayInnerStyle={{minWidth: "350px"}} title="Die Änderung des Templates führt zur Aktualisierung aller bereits verknüpften Labels.">
                <InfoCircleOutlined style={{marginLeft: 3}}/>
              </Tooltip>
            </>
          }
          name="template"
          rules={[{ required: true, message: "Bitte Template auswählen" }]}
        >
          <Select
            showSearch
            placeholder="Template auswählen"
            value={form.getFieldValue("template")?.value || undefined}
            onChange={(value, option) => handleTemplateChange(value, option)}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={[
              ...new Map(templates?.filter(template => template?.locations?.includes(customer?.sepiooLocation))
                .map((item) => [item.name, item])).values(),
            ].map(item => ({
              value: item.id,
              label: item.name,
            }))}
          />

          {(() => {
            const template = templates?.find(template => template?.name === selectedTemplate);

            return <>
              <div
                style={{marginTop: "10px"}}
              >
                <TemplatePreview 
                  templateName={template?.name}
                  sourceCode={template?.sourceCode} 
                />
              </div>
            </>
          })()}
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Bestätigen
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EventModal;
